import { useEffect, useMemo } from "react";
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";

const PREFERRED_COLUMNS = ["source_filename", "Date", "Amount", "Description"];

const formatCellValue = (key, value) => {
	if (value == null || value === "") return "-";
	if (key === "Amount" && typeof value === "number") {
		return value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
	}
	return String(value);
};

const normalizePreviewResponse = (previewData) => {
	if (previewData == null) return null;

	const result = previewData.result ?? previewData;
	const data = Array.isArray(result?.data) ? result.data : [];

	const columnKeys = data.reduce((keys, row) => {
		Object.keys(row || {}).forEach((key) => {
			if (!keys.includes(key)) keys.push(key);
		});
		return keys;
	}, []);

	const orderedColumns = [
		...PREFERRED_COLUMNS.filter((key) => columnKeys.includes(key)),
		...columnKeys.filter((key) => !PREFERRED_COLUMNS.includes(key)),
	];

	return {
		status: previewData.status ?? "Unknown",
		message: previewData.message ?? "",
		rowCount: result?.row_count ?? data.length,
		filesUploaded: result?.files_uploaded ?? 0,
		filesProcessed: result?.files_processed ?? 0,
		filesFailed: result?.files_failed ?? 0,
		errors: Array.isArray(result?.errors) ? result.errors : [],
		data,
		columns: orderedColumns,
		isSuccess: String(previewData.status ?? "").toLowerCase() === "success",
	};
};

const PayByLinkPreviewDialog = ({ open, onClose, previewData, images = [], onApprove, approving }) => {
	const preview = useMemo(() => normalizePreviewResponse(previewData), [previewData]);

	const imagePreviews = useMemo(
		() =>
			images.map((file) => ({
				file,
				url: URL.createObjectURL(file),
			})),
		[images]
	);

	useEffect(() => {
		return () => {
			imagePreviews.forEach(({ url }) => URL.revokeObjectURL(url));
		};
	}, [imagePreviews]);

	const canApprove =
		preview != null && preview.isSuccess && preview.data.length > 0 && preview.filesFailed === 0;

	return (
		<Dialog open={open} onClose={approving ? undefined : onClose} fullWidth maxWidth="lg">
			<DialogTitle sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", pr: 1.5 }}>
				<Box>
					<Typography variant="h6" fontWeight={600}>
						Review Pay By Link data
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
						Verify the extracted details below. Approve to save the images and data.
					</Typography>
				</Box>
				<IconButton onClick={onClose} disabled={approving} aria-label="Close">
					<CloseRoundedIcon />
				</IconButton>
			</DialogTitle>

			<DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
				{preview && (
					<Alert severity={preview.isSuccess ? "success" : "error"} sx={{ borderRadius: 1.5 }}>
						<Typography variant="subtitle2" fontWeight={600}>
							{preview.status}
						</Typography>
						{preview.message && (
							<Typography variant="body2" sx={{ mt: 0.25 }}>
								{preview.message}
							</Typography>
						)}
					</Alert>
				)}

				{preview && (
					<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
						<Chip label={`${preview.rowCount} rows`} size="small" variant="outlined" />
						<Chip label={`${preview.filesUploaded} uploaded`} size="small" variant="outlined" />
						<Chip label={`${preview.filesProcessed} processed`} size="small" variant="outlined" color="success" />
						{preview.filesFailed > 0 && (
							<Chip label={`${preview.filesFailed} failed`} size="small" variant="outlined" color="error" />
						)}
						<Chip label="Preview" size="small" variant="outlined" color="warning" />
					</Box>
				)}

				{preview?.errors.length > 0 && (
					<Alert severity="error" sx={{ borderRadius: 1.5 }}>
						{preview.errors.map((error, index) => (
							<Typography key={index} variant="body2">
								{typeof error === "string" ? error : JSON.stringify(error)}
							</Typography>
						))}
					</Alert>
				)}

				{imagePreviews.length > 0 && (
					<Box>
						<Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
							Uploaded images ({imagePreviews.length})
						</Typography>
						<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
							{imagePreviews.map(({ file, url }) => (
								<Paper
									key={`${file.name}-${file.lastModified}`}
									elevation={0}
									sx={{
										width: 96,
										borderRadius: 1.5,
										border: "1px solid",
										borderColor: "divider",
										overflow: "hidden",
									}}>
									<Box
										component="img"
										src={url}
										alt={file.name.replace(/^PAY_BY_LINK-/, "")}
										sx={{ width: "100%", height: 72, objectFit: "cover", display: "block" }}
									/>
									<Typography
										variant="caption"
										sx={{
											display: "block",
											px: 0.75,
											py: 0.5,
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap",
										}}
										title={file.name.replace(/^PAY_BY_LINK-/, "")}>
										{file.name.replace(/^PAY_BY_LINK-/, "")}
									</Typography>
								</Paper>
							))}
						</Box>
					</Box>
				)}

				<Box>
					<Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
						Extracted data
					</Typography>
					<TableContainer
						component={Paper}
						elevation={0}
						sx={{
							borderRadius: 1.5,
							border: "1px solid",
							borderColor: "divider",
							maxHeight: 360,
						}}>
						<Table size="small" stickyHeader>
							<TableHead>
								<TableRow>
									{(preview?.columns ?? PREFERRED_COLUMNS).map((column) => (
										<TableCell key={column} sx={{ fontWeight: 600, bgcolor: "grey.50" }}>
											{column.replace(/_/g, " ")}
										</TableCell>
									))}
								</TableRow>
							</TableHead>
							<TableBody>
								{preview?.data.length ? (
									preview.data.map((row, rowIndex) => (
										<TableRow key={`${row.source_filename ?? "row"}-${rowIndex}`} hover>
											{preview.columns.map((column) => (
												<TableCell key={`${rowIndex}-${column}`}>
													{formatCellValue(column, row[column])}
												</TableCell>
											))}
										</TableRow>
									))
								) : (
									<TableRow>
										<TableCell colSpan={preview?.columns.length || PREFERRED_COLUMNS.length}>
											<Typography variant="body2" color="text.secondary">
												No extracted rows returned.
											</Typography>
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</TableContainer>
				</Box>
			</DialogContent>

			<DialogActions sx={{ px: 3, py: 2 }}>
				<Button onClick={onClose} disabled={approving} sx={{ textTransform: "none", fontWeight: 600 }}>
					Cancel
				</Button>
				<Button
					variant="contained"
					disableElevation
					onClick={onApprove}
					disabled={approving || !canApprove}
					startIcon={
						approving ? <CircularProgress size={18} color="inherit" /> : <CheckCircleOutlineRoundedIcon />
					}
					sx={{ textTransform: "none", fontWeight: 600, px: 3 }}>
					{approving ? "Saving..." : "Approve & save"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default PayByLinkPreviewDialog;
